import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import type { PatientInvoiceKind } from '@/model/middleware-types'

// Data layer of the self-contained VisitCoPayPanel. The status query polls
// while a payment link is awaiting so webhook-driven changes surface without
// user action; every mutation refreshes the status.
export function useCopay(encounterId: string | null, kind: PatientInvoiceKind = 'copay') {
  const orpc = useOrpc()
  const queryClient = useQueryClient()

  const statusQuery = useQuery(
    orpc.getCopayStatus.queryOptions({
      input: { encounterId: encounterId ?? '', kind },
      enabled: Boolean(encounterId),
      refetchInterval: (query) => (query.state.data?.status === 'awaiting' ? 5_000 : false),
    }),
  )

  const invalidateStatus = () => {
    void queryClient.invalidateQueries({ queryKey: orpc.getCopayStatus.key() })
  }

  const createCheckout = useMutation(
    orpc.createCopayCheckout.mutationOptions({ onSuccess: invalidateStatus }),
  )
  const sendLink = useMutation(orpc.sendCopayLink.mutationOptions({ onSuccess: invalidateStatus }))
  const syncStatus = useMutation(
    orpc.syncCopayStatus.mutationOptions({ onSuccess: invalidateStatus }),
  )
  const refund = useMutation(orpc.refundCopay.mutationOptions({ onSuccess: invalidateStatus }))
  const resendReceipt = useMutation(
    orpc.resendCopayReceipt.mutationOptions({ onSuccess: invalidateStatus }),
  )

  return { statusQuery, createCheckout, sendLink, syncStatus, refund, resendReceipt }
}
